import { api, devAgentAuthHeaders, unwrap, withUserId } from "./core";
import type { MetadataItem } from "./types";
import { useAuthStoreV1 } from "@/stores/authStore_v1";

function artifactTypeToMetadataType(artifactType: string): string {
  if (artifactType === "apex_trigger") return "ApexTrigger";
  if (artifactType === "visualforce_page") return "ApexPage";
  if (
    artifactType === "lwc_bundle" ||
    artifactType === "lwc_html" ||
    artifactType === "lwc_js"
  ) {
    return "LightningComponentBundle";
  }
  if (artifactType === "aura_bundle") return "AuraDefinitionBundle";
  if (artifactType === "flow_definition") return "Flow";
  return "ApexClass";
}

export const getMetadataItemById = async (
  id: string,
  orgId?: string,
): Promise<MetadataItem> => getMetadataItem(id, orgId);

export const getMetadataItem = async (
  id: string,
  orgId?: string,
): Promise<MetadataItem> => {
  if (id.startsWith("art_")) {
    const artifact = unwrap<{
      id: string;
      name?: string;
      type?: string;
      content?: string;
      language?: string | null;
    }>(
      (
        await api.get(withUserId(`/api/artifacts/${encodeURIComponent(id)}`), {
          headers: devAgentAuthHeaders(orgId),
        })
      ).data,
    );

    return {
      id: artifact.id,
      full_name: artifact.name || artifact.id,
      name: artifact.name || artifact.id,
      metadata_type: artifactTypeToMetadataType(artifact.type || ""),
      content: artifact.content || "",
      language: artifact.language || undefined,
    };
  }

  const resolvedOrgId = orgId || useAuthStoreV1.getState().selectedOrg?.id || "";
  const params = resolvedOrgId ? `?org_id=${encodeURIComponent(resolvedOrgId)}` : "";
  return unwrap(
    (
      await api.get(withUserId(`/api/chat/metadata/items/${encodeURIComponent(id)}${params}`), {
        headers: devAgentAuthHeaders(resolvedOrgId),
      })
    ).data,
  );
};

export const getMetadataItemByName = async (
  name: string,
  orgId?: string,
  silent = false,
): Promise<MetadataItem> => {
  const resolvedOrgId = orgId || useAuthStoreV1.getState().selectedOrg?.id || "";
  const params = resolvedOrgId ? `?org_id=${encodeURIComponent(resolvedOrgId)}` : "";
  return unwrap(
    (
      await api.get(
        withUserId(`/api/chat/metadata/items/by-name/${encodeURIComponent(name)}${params}`),
        { silent, headers: devAgentAuthHeaders(resolvedOrgId) } as any,
      )
    ).data,
  );
};

export const listMetadataTypes = async (
  orgId: string,
): Promise<{ types: Array<{ type: string; count: number }> }> =>
  unwrap(
    (
      await api.get(withUserId(`/api/chat/metadata/types?org_id=${encodeURIComponent(orgId)}`), {
        headers: devAgentAuthHeaders(orgId),
      })
    ).data,
  );

export const listMetadataItems = async (
  orgId: string,
  params: { type: string; limit?: number; offset?: number },
): Promise<{ items: MetadataItem[] }> => {
  const query = new URLSearchParams({ org_id: orgId, type: params.type });
  if (params.limit != null) query.append("limit", String(params.limit));
  if (params.offset != null) query.append("offset", String(params.offset));
  return unwrap(
    (
      await api.get(withUserId(`/api/chat/metadata/items?${query}`), {
        headers: devAgentAuthHeaders(orgId),
      })
    ).data,
  );
};
